import { Button, Checkbox } from '@shared/ui/kit';
import InputSearch from '@shared/ui/input-search';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { fonts } from '@shared/tokens/fonts.stylex';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FilterDropdownSize, getFilterDropdownStyle } from '../consts';
import styles from '../FilterDropdown/FilterDropdown.module.scss';

const resetFilterButtonSettings = {
  fontSize: '14px',
  fontWeight: '400',
  fontFamily: fonts.din2014Regular,
};

export interface FilterItem<T extends string | number> {
  value: T;
  label: string;
}

interface SearchCheckboxFilterDropdownProps<T extends string | number> {
  items: FilterItem<T>[];
  selectedValues: T[];
  onApply: (values: T[]) => void;
  onClose: () => void;
  selectAllLabel: string;
  size: FilterDropdownSize;
  variant?: 'default' | 'compact';
  afterSearch?: ReactNode;
  onReset?: () => void;
  isResetActive?: boolean;
  onSelectedValuesChange?: (values: T[]) => void;
}

export const SearchCheckboxFilterDropdown = <T extends string | number>({
  items,
  selectedValues: initialSelectedValues,
  onApply,
  onClose,
  selectAllLabel,
  size,
  variant = 'default',
  afterSearch,
  onReset,
  isResetActive = false,
  onSelectedValuesChange,
}: SearchCheckboxFilterDropdownProps<T>) => {
  const isCompact = variant === 'compact';
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedValues, setSelectedValues] = useState<T[]>(initialSelectedValues);

  const filteredItems = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();

    if (!normalizedSearchValue) return items;

    return items.filter(({ label }) => label.toLowerCase().includes(normalizedSearchValue));
  }, [items, searchValue]);

  const filteredValues = filteredItems.map(({ value }) => value);
  const selectedFilteredCount = filteredValues.filter((value) => selectedValues.includes(value)).length;
  const isAllFilteredSelected = !!filteredItems.length && selectedFilteredCount === filteredItems.length;
  const isSomeFilteredSelected = selectedFilteredCount > 0 && !isAllFilteredSelected;
  const activeFiltersCount = [selectedValues.length, searchValue.trim()].filter(Boolean).length;
  const isFilterEmpty = !activeFiltersCount && !isResetActive;

  useEffect(() => {
    setSelectedValues(initialSelectedValues);
  }, [initialSelectedValues]);

  const onResetFilter = () => {
    setSearchValue('');
    setSelectedValues([]);
    onSelectedValuesChange?.([]);

    if (onReset) {
      onReset();
      return;
    }

    onApply([]);
  };

  const updateSelectedValues = (values: T[]) => {
    setSelectedValues(values);
    onSelectedValuesChange?.(values);

    if (isCompact) {
      onApply(values);
    }
  };

  const onToggleAll = () => {
    if (isAllFilteredSelected) {
      updateSelectedValues(selectedValues.filter((value) => !filteredValues.includes(value)));
      return;
    }

    updateSelectedValues([...new Set([...selectedValues, ...filteredValues])]);
  };

  const onToggleItem = (value: T) => {
    if (selectedValues.includes(value)) {
      updateSelectedValues(selectedValues.filter((item) => item !== value));
      return;
    }

    updateSelectedValues([...selectedValues, value]);
  };

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (dropdownRef.current.contains(event.target as Node)) return;
      onClose();
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
    };
  }, [onClose]);

  return (
    <div className={styles.filterDropdown} style={getFilterDropdownStyle(size)} ref={dropdownRef}>
      {!isCompact && (
        <div className={styles.filterDropdownHeader}>
          <span className={styles.filterDropdownTitle}>
            Фильтры
            {!!activeFiltersCount && <span className={styles.filterCountBadge}>{activeFiltersCount}</span>}
          </span>
          <Button
            variant="color"
            size="small"
            onClick={onResetFilter}
            isDisabled={isFilterEmpty}
            settings={resetFilterButtonSettings}
          >
            Сбросить фильтры
          </Button>
        </div>
      )}

      <div className={styles.filterDropdownContent}>
        <div className={styles.filterDropdownSearchGroup}>
          <InputSearch searchValue={searchValue} changeValue={setSearchValue} clearHandler={() => setSearchValue('')} />
          {afterSearch}
          {afterSearch && <div className={styles.filterOptionsDivider} />}
        </div>

        <div className={styles.filterDropdownScroll}>
          <div className={styles.filterOptions}>
            <UnstyledButton className={styles.filterOption} onClick={onToggleAll}>
              <Checkbox
                size="small"
                isChecked={isAllFilteredSelected}
                isIndeterminate={isSomeFilteredSelected}
                onChange={() => {}}
              />
              <span className={styles.filterOptionText}>{selectAllLabel}</span>
            </UnstyledButton>

            <div className={styles.filterOptionsDivider} />

            <div className={styles.filterProjectList}>
              {filteredItems.map(({ value, label }) => (
                <UnstyledButton key={String(value)} className={styles.filterOption} onClick={() => onToggleItem(value)}>
                  <Checkbox size="small" isChecked={selectedValues.includes(value)} onChange={() => {}} />
                  <span className={styles.filterOptionText}>{label}</span>
                </UnstyledButton>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.filterDropdownFooter}>
        {isCompact ? (
          <div className={styles.filterFooterApply}>
            <Button
              variant="color"
              size="small"
              onClick={onResetFilter}
              isDisabled={isFilterEmpty}
              settings={resetFilterButtonSettings}
            >
              Сбросить фильтры
            </Button>
          </div>
        ) : (
          <>
            <div className={styles.filterFooterApply}>
              <Button variant="accent" size="small" onClick={() => onApply(selectedValues)}>
                Применить
              </Button>
            </div>
            <div className={styles.filterFooterClose}>
              <Button variant="link" size="small" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
